import { Td } from 'components/Td/Td'
import { CircleButton, ScheduleMonthlyCard, ScheduleOption, ScheduleOptionsWrapper } from 'pages/Shifts/Shifts.styled'
import { Flex, ProfilePicture, Table, TableBody, TableWrapper, Th, Tr } from 'styling/GlobalStyles'
import { addZero } from 'utils/helpers'
import { BodyText, OtherText } from 'styling/Typography'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Avatar from '../../assets/images/profileAvatar.png'
import { AppDispatch, RootState } from 'store'
import { setConfirmModal } from 'features/global/globalSlice'
import { addWorkSchedule, addWorkScheduleArray, clearWorkSchedule, getWorkSchedules, removeWorkSchedule } from 'features/work-schedules/workScheduleSlice'
import { ISingleEmployeeShift, ISingleShift } from 'types/shifts.interface'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { clearShiftsList, getShifts } from 'features/shifts/shiftsSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { IGetAllWorkSchedulesSingleResponse } from 'types/api/workScheduleApi.interface'
import { CheckBox } from 'components/CheckBox/CheckBox'


export const ScheduleMonthlyDisplay = ({ params, workSchedules }: { params: any, workSchedules: IGetAllWorkSchedulesSingleResponse[] }) => {
    const [checked, setChecked] = useState([])
    const [openDropdown, setOpenDropdown] = useState<{ userId: number, day: number } | null>(null)
    const dispatch = useDispatch<AppDispatch>()
    const { shifts } = useSelector((store: RootState) => store.shifts)
    const [isOutHouse, setIsOutHouse] = useState(false)

    useQueryPagination({ fetchFunctions: [getWorkSchedules], cleanUpFunctions: [clearWorkSchedule], params, otherFunctions: [() => setChecked([])], dontFetchIfEmpty: ['dateTo', 'dateFrom'] })

    useEffect(() => {
        dispatch(getShifts({ page: 1, limit: 99 }))
        return () => {
            dispatch(clearShiftsList())
        }
    }, [])

    const monthlyHeaderDays = useMemo(() => {
        const arr = []
        const date = new Date(params.dateFrom)
        const year = date.getFullYear()
        const month = date.getMonth()
        const day = new Date(year, month, 1)
        const daysInMonth = new Date(year, month + 1, 0).getDate()

        for (let i = 1; i <= daysInMonth; i++) {
            const dayOfWeek = day.getDay()
            const currentDay = `${addZero(day.getDate())}. ${addZero(day.getMonth() + 1)}.`
            arr.push(
                <Td
                    noTooltip
                    w='29px'
                    skew
                    key={`header-${year}-${month}-${i}`}
                    color={dayOfWeek == 0 || dayOfWeek == 6 ? '#ff9900' : null}
                    text={currentDay}
                />,
            )
            day.setDate(day.getDate() + 1)
        }

        return arr
    }, [params.dateFrom])

    interface ShiftWithDate {
        date: string;
        shift: ISingleShift;
    }

    const handleOuthouse = (e, v: boolean) => {
        e.stopPropagation()
        setIsOutHouse(v)
    }

    const handleWorkSchedule = (shiftId: number, userId: number, date) => {
        if (!shiftId) {
            dispatch(
                setConfirmModal({
                    isOpen: true,
                    onSave: async () => {
                        await dispatch(removeWorkSchedule({ userId, date }))
                    },
                    word: 'unassign shift',
                }),
            )

            return
        }
        setChecked([])

        checked.length < 2
            ? dispatch(addWorkSchedule({ userId, date, shiftId, isOutHouse }))
            : dispatch(addWorkScheduleArray({ users: checked, date, shiftId, isOutHouse }))
    }

    const renderShiftInputs = (dates: ShiftWithDate[], userId: number, up?: boolean) => {
        const arr = []
        const day = new Date(params.dateFrom)
        const date = new Date(params.dateFrom)
        const year = date.getFullYear()
        const month = date.getMonth()
        const daysInMonth = new Date(year, month + 1, 0).getDate()
        // Precompute a map for O(1) lookups instead of Array.find per day
        const dateToShiftMap = new Map<number, ShiftWithDate>()
        for (const d of dates) {
            dateToShiftMap.set(new Date(d.date).setHours(0, 0, 0, 0), d)
        }
        for (let i = 1; i <= daysInMonth; i++) {
            const shiftForTheDay: ShiftWithDate | undefined = dateToShiftMap.get(new Date(day).setHours(0, 0, 0, 0))
            const handleDropdown = () => {
                if (openDropdown && openDropdown.userId === userId && openDropdown.day === i) {
                    setOpenDropdown(null)
                } else {
                    setOpenDropdown({ userId, day: i })
                }
            }
            const color = shiftForTheDay?.shift?.color ? '#' + shiftForTheDay?.shift.color : '#ff9900'

            const getDay = () => {
                const dt = new Date(params.dateFrom)
                dt.setDate(dt.getDate() + i - 1)
                return dt
            }

            arr.push(
                <Td key={`${userId}-${i}`} noTooltip>
                    <ScheduleMonthlyCard color={shiftForTheDay?.shift ? color : null} tabIndex={1} onBlur={() => setOpenDropdown(null)} onClick={handleDropdown}>
                        <BodyText
                            style={{
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                maxWidth: '100%',
                            }}
                            weight='bold' color={shiftForTheDay?.shift ? 'black' : 'white'} >{shiftForTheDay?.shift?.name ? shiftForTheDay?.shift?.name[0] : '/'}
                        </BodyText>
                        {openDropdown && openDropdown.userId === userId && openDropdown.day === i && (
                            <ScheduleOptionsWrapper up={up} right={day.getDate() >= 5} >
                                {shiftForTheDay?.shift && <ScheduleOption onClick={() => handleWorkSchedule(null, userId, getDay())} >
                                    <OtherText color='red' >Remove Shift</OtherText>
                                </ScheduleOption>}
                                {shifts.map(s => <ScheduleOption onClick={() => handleWorkSchedule(s.id, userId, getDay())} key={s.id}>
                                    <OtherText>{s.name}</OtherText>
                                </ScheduleOption>)}
                                <Flex width='100%' column padding='0 1rem' >
                                    <ScheduleOption onClick={(e) => handleOuthouse(e, false)} style={{ borderTop: '1px solid white' }} >
                                        <CheckBox state={!isOutHouse} title='Inhouse' />
                                    </ScheduleOption>
                                    <ScheduleOption onClick={(e) => handleOuthouse(e, true)} >
                                        <CheckBox state={isOutHouse} title='Outhouse' />
                                    </ScheduleOption>
                                </Flex>
                            </ScheduleOptionsWrapper>
                        )}

                    </ScheduleMonthlyCard>
                </Td>,
            )
            day.setDate(day.getDate() + 1)
        }

        return arr
    }

    const toggle = (id: number) => {
        if (checked.includes(id)) {
            setChecked((prev) => {
                return prev.filter((item) => item !== id)
            })
        } else {
            setChecked((prev) => (!prev ? [id] : [...prev, id]))
        }
    }

    const toggleAll = () => {
        if (checked.length < workSchedules.length) {
            const allIds = workSchedules.map((a) => a.id)
            setChecked(allIds)
        } else {
            setChecked([])
        }
    }

    return <TableWrapper style={{ height: '100%' }}>
        <Table style={{ height: '100%' }}>
            <Th>
                <Td w='3%' noTooltip>
                    <CircleButton
                        onClick={toggleAll}
                        checked={checked.length === workSchedules?.length}
                    />
                </Td>
                <Td w='27%' text='Employee' noTooltip />
                {monthlyHeaderDays}
            </Th>
            <TableBody>
                {workSchedules?.length ?
                    workSchedules.map((item: IGetAllWorkSchedulesSingleResponse, i: number) => {
                        const id = item.id
                        return (
                            <Tr borderHover key={id}>
                                <Td noTooltip w='30%'>
                                    <Flex center gap='1rem'>
                                        <CircleButton
                                            orange
                                            checked={checked.includes(id)}
                                            onClick={() => toggle(id)}
                                        />
                                        <ProfilePicture >
                                            <img
                                                src={item.profileImageUrl || Avatar}
                                                alt=''
                                                onError={({ currentTarget }) => {
                                                    currentTarget.onerror = null
                                                    currentTarget.src = Avatar
                                                }}
                                            />
                                        </ProfilePicture>
                                        <BodyText>{item.firstName + ' ' + item.lastName}</BodyText>
                                    </Flex>
                                </Td>
                                {renderShiftInputs(item.schedules, item.id, (workSchedules.length > 5) && (i >= workSchedules.length - 5))}
                            </Tr>
                        )
                    }) : <NoItems title='Employees' />}
            </TableBody>
        </Table>
    </TableWrapper>
}